/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/ClientAnalysisImportModal.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useEffect, useState } from 'react';
import {
  recognizeApps, importClientAnalysis, type ReconcileMode,
} from '../compat/aiRunsApi';
import { listKnowledgeFiles, uploadKnowledgeFiles, type KnowledgeFile } from '../compat/aiKnowledgeApi';

/**
 * Import a client-provided analysis (Word / PPT / Excel) and interpret it into
 * structured per-app dispositions. Flow: pick or upload a document → detect the
 * applications it mentions → choose a reconciliation mode per app → interpret.
 */
type Step = 'pick' | 'reconcile' | 'submitting' | 'done';

interface MatchRow {
  id: string;
  code: string | null;
  name: string;
  include: boolean;
  mode: ReconcileMode;
  proposed?: boolean; // came from a lower-confidence proposal (double-check before running)
}

const MODE_LABEL: Record<ReconcileMode, string> = {
  overwrite: 'Overwrite — make client analysis current',
  consolidate: 'Consolidate — merge with existing AI analysis',
  keep: 'Keep as alternative (do not pin)',
};

export default function ClientAnalysisImportModal({ onClose, onDone }: { onClose: () => void; onDone: (runId: string) => void; }) {
  const [step, setStep] = useState<Step>('pick');
  const [docMode, setDocMode] = useState<'existing' | 'upload'>('existing');
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [crossPortfolio, setCrossPortfolio] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [runId, setRunId] = useState('');

  useEffect(() => { void refreshFiles(); }, []);
  async function refreshFiles() {
    try { setFiles(await listKnowledgeFiles()); }
    catch { setError('Failed to load documents from the Knowledge Library.'); }
  }

  const selectedFile = files.find(f => f.id === selectedFileId);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('files', file);
      fd.append('tags', 'client-analysis');
      const created = await uploadKnowledgeFiles(fd);
      await refreshFiles();
      if (created[0]?.id) setSelectedFileId(created[0].id);
      if (!name.trim()) setName(`Client analysis — ${file.name}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed.');
    } finally { setUploading(false); }
  }

  async function detect() {
    if (!selectedFileId) { setError('Choose or upload a document first.'); return; }
    setBusy(true); setError('');
    try {
      const res = await recognizeApps(selectedFileId);
      // Build the candidate list from BOTH confident matches AND proposals, and
      // expand parent (roll-up) apps into their analyzable child modules — the
      // parent itself (e.g. "SAP ECC 6.0") can't be dispositioned directly.
      const seen = new Set<string>();
      const rows: MatchRow[] = [];
      const addApp = (id: string, code: string | null, name: string, include: boolean, proposed: boolean) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        rows.push({ id, code: code ?? null, name, include, mode: 'overwrite', proposed });
      };
      const addEntry = (e: any, proposed: boolean, include: boolean) => {
        if (!e || e.excluded_from_apr) return;
        if (e.is_parent) (e.children || []).forEach((c: any) => addApp(c.id, c.application_id_code, c.name, include, proposed));
        else addApp(e.id, e.application_id_code, e.name, include, proposed);
      };
      (res.matched || []).forEach((m: any) => addEntry(m, false, true));
      (res.proposals || []).forEach((p: any) => {
        const c = (p.candidates || [])[0];
        // Default-include a strong proposal (score ≥ 0.5), otherwise offer it unchecked.
        if (c) addEntry(c, true, Number(c.score ?? 0) >= 0.5);
      });
      if (!rows.length) {
        setError('No portfolio applications were detected in this document. Check that the application names in the document match your master data.');
        setBusy(false);
        return;
      }
      setMatches(rows);
      setStep('reconcile');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to detect applications.');
    } finally { setBusy(false); }
  }

  async function submit() {
    const apps = matches.filter(m => m.include).map(m => ({ applicationId: m.id, mode: m.mode }));
    if (!apps.length) { setError('Include at least one application.'); return; }
    setStep('submitting'); setError('');
    try {
      const run = await importClientAnalysis({
        knowledgeFileId: selectedFileId,
        name: name.trim() || undefined,
        clientName: clientName.trim() || undefined,
        additionalComments: instructions.trim() || undefined,
        crossPortfolio,
        apps,
      });
      setRunId(run.id);
      setStep('done');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to start the client analysis.');
      setStep('reconcile');
    }
  }

  const setRow = (id: string, patch: Partial<MatchRow>) =>
    setMatches(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#1f2a44' }}>Import client analysis</h2>
          <button className="rd-btn rd-btn-ghost" onClick={onClose}>Close</button>
        </div>
        <p style={{ marginTop: 0, color: '#667', fontSize: 13.5 }}>
          Upload the client's own analysis (Word, PowerPoint or Excel). We detect the applications it mentions and interpret it into structured dispositions you can pin or merge.
        </p>

        {error && <div style={errorBox}>{error}</div>}

        {step === 'pick' && (
          <>
            <label style={lbl}>Document source</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button type="button" className={docMode === 'existing' ? 'rd-btn rd-btn-primary' : 'rd-btn rd-btn-ghost'} onClick={() => setDocMode('existing')}>
                Select existing
              </button>
              <button type="button" className={docMode === 'upload' ? 'rd-btn rd-btn-primary' : 'rd-btn rd-btn-ghost'} onClick={() => setDocMode('upload')}>
                Upload new
              </button>
            </div>

            {docMode === 'existing' ? (
              <select className="rd-select" style={{ width: '100%' }} value={selectedFileId} onChange={e => setSelectedFileId(e.target.value)}>
                <option value="">— choose a document from the Knowledge Library —</option>
                {files.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.file_name}{f.text_extraction_status && f.text_extraction_status !== 'ok' ? '  (⚠ not readable)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div style={{ border: '1px dashed #c7d0dd', borderRadius: 8, padding: '14px 16px', background: '#f8fafc' }}>
                <input
                  type="file"
                  accept=".pdf,.docx,.xlsx,.pptx,.ppt,.csv,.txt,.md"
                  onChange={handleUpload}
                  disabled={uploading}
                  style={{ fontSize: 13 }}
                />
                <div style={{ fontSize: 12, color: '#8a94a6', marginTop: 8 }}>
                  Word, PowerPoint, Excel, PDF or text. {uploading ? 'Uploading…' : ''}
                </div>
                {selectedFile && !uploading && (
                  <div style={{ fontSize: 12.5, color: '#17914f', marginTop: 6 }}>
                    ✓ Uploaded: <strong>{selectedFile.file_name}</strong>
                  </div>
                )}
              </div>
            )}
            {selectedFile && selectedFile.text_extraction_status && selectedFile.text_extraction_status !== 'ok' && (
              <div style={warnBox}>This document's text could not be extracted, so interpretation may fail. Try a text-based Word/PDF export.</div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Analysis name (optional)</label>
                <input className="rd-input" style={{ width: '100%' }} value={name} onChange={e => setName(e.target.value)} placeholder="Client analysis — Q3 findings" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Client / company (optional)</label>
                <input className="rd-input" style={{ width: '100%' }} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Meridian Industrial Group" />
              </div>
            </div>

            <label style={lbl}>Additional Analysis Instructions — what is the client trying to achieve?</label>
            <textarea
              className="rd-input"
              style={{ width: '100%', minHeight: 72, resize: 'vertical' }}
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              placeholder="e.g. Consolidate the US and EU SAP ECC instances into a single S/4HANA; recommend Greenfield vs Brownfield by user count and customization; scan the portfolio for other ERP / middleware (Oracle, MuleSoft, Dynamics) to harmonize."
            />

            <label style={lbl}>Analysis scope</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={radioRow}>
                <input type="radio" name="ca-scope" checked={crossPortfolio} onChange={() => setCrossPortfolio(true)} />
                <span><strong>Document + portfolio consolidation scan</strong> — interpret the document <em>and</em> look across every analyzed application for consolidation / harmonization opportunities. <span style={{ color: '#8a94a6' }}>(recommended)</span></span>
              </label>
              <label style={radioRow}>
                <input type="radio" name="ca-scope" checked={!crossPortfolio} onChange={() => setCrossPortfolio(false)} />
                <span><strong>Document only</strong> — interpret just what this document covers.</span>
              </label>
            </div>

            <div style={driveNote}>
              <strong>You're in the driving seat.</strong> The analysis is anchored to the client's document — and its depth is driven by what you ask for. The more specific your instructions and requirements (targets, constraints, systems to harmonize), the richer and more tailored the result; vague input yields a thin analysis.
            </div>

            <div style={footer}>
              <button className="rd-btn rd-btn-ghost" onClick={onClose}>Cancel</button>
              <button className="rd-btn rd-btn-primary" disabled={!selectedFileId || busy} onClick={detect}>
                {busy ? 'Detecting…' : 'Detect applications →'}
              </button>
            </div>
          </>
        )}

        {step === 'reconcile' && (
          <>
            <p style={{ fontSize: 13.5, color: '#445' }}>
              We detected <strong>{matches.length}</strong> application{matches.length === 1 ? '' : 's'} mentioned in <strong>{selectedFile?.file_name}</strong>. Choose what to do with each.
            </p>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e9f0', borderRadius: 8 }}>
              <table className="rd-table" style={{ margin: 0 }}>
                <thead>
                  <tr><th style={{ width: 34 }}></th><th>Application</th><th>Reconciliation</th></tr>
                </thead>
                <tbody>
                  {matches.map(m => (
                    <tr key={m.id}>
                      <td><input type="checkbox" checked={m.include} onChange={e => setRow(m.id, { include: e.target.checked })} /></td>
                      <td>
                        {m.code && <span style={{ fontWeight: 700, color: '#1565c0', marginRight: 6 }}>{m.code}</span>}
                        {m.name}
                        {m.proposed && <span title="Lower-confidence match — please confirm" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '1px 6px' }}>proposed</span>}
                      </td>
                      <td>
                        <select className="rd-select" value={m.mode} disabled={!m.include}
                          onChange={e => setRow(m.id, { mode: e.target.value as ReconcileMode })}>
                          {(Object.keys(MODE_LABEL) as ReconcileMode[]).map(k => (
                            <option key={k} value={k}>{MODE_LABEL[k]}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={footer}>
              <button className="rd-btn rd-btn-ghost" onClick={() => setStep('pick')}>← Back</button>
              <button className="rd-btn rd-btn-primary" onClick={submit}>Interpret analysis</button>
            </div>
          </>
        )}

        {step === 'submitting' && (
          <div style={{ padding: 30, textAlign: 'center', color: '#667' }}>Starting the client-analysis run…</div>
        )}

        {step === 'done' && (
          <div style={{ padding: '20px 4px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, color: '#17914f', fontWeight: 600, marginBottom: 8 }}>Client analysis started.</div>
            <p style={{ fontSize: 13.5, color: '#556' }}>The interpreter is running in the background. Open the run to watch progress and review the results.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 8 }}>
              <button className="rd-btn rd-btn-ghost" onClick={onClose}>Close</button>
              <button className="rd-btn rd-btn-primary" onClick={() => onDone(runId)}>Open the run</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(20,30,50,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '6vh 16px' };
const card: React.CSSProperties = { background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,.3)', padding: 22, width: 'min(720px, 96vw)', maxHeight: '88vh', overflowY: 'auto' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#556', margin: '10px 0 4px' };
const footer: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', marginTop: 18, gap: 10 };
const errorBox: React.CSSProperties = { background: '#fdecea', border: '1px solid #f5c6c0', color: '#b3261e', borderRadius: 8, padding: '10px 12px', fontSize: 13, margin: '4px 0 12px' };
const warnBox: React.CSSProperties = { background: '#fff8e1', border: '1px solid #ffe082', color: '#8a6d00', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginTop: 8 };
const radioRow: React.CSSProperties = { display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, color: '#374151', cursor: 'pointer', lineHeight: 1.4 };
const driveNote: React.CSSProperties = { marginTop: 12, background: '#eef4ff', border: '1px solid #cfe0f7', borderRadius: 8, padding: '10px 13px', fontSize: 12.5, color: '#1d3a5f', lineHeight: 1.5 };
